import React, { useEffect, useState } from "react";
import "./NewsForm.css";
import { newsService } from "../../services/newsService";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import MediaLibraryModal from "../../components/common/MediaLibrary/MediaLibraryModal";
import api from "../../services/api";

const CorporateAnnouncementForm = ({ announcementId, onSubmitted, onCancel }) => {

  const [formData, setFormData] = useState({
    publisher: "NSE Corporate Filing",
    title: "",
    link: "",
    publication_date: "",
    publish_time: "",
    brief_description: "",
    news_content: "",
    tags_stocks: [],
    image_url: null,
    credit: "NSE",
    source_url: "",
    destination_url: "",
    tags_sectors: [],
    tags_industries: [],
    news_category: "",
    ntf_type: "",
    premium: false,
  });

  const [announcementData, setAnnouncementData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [internalTokens, setInternalTokens] = useState([]);
  const [tagSectors, setTagSectors] = useState([]);
  const [tagIndustries, setTagIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [newsCategory, setNewsCategory] = useState("");

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file) {
        setFormData({ ...formData, image_url: file });
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleMediaSelect = (imageName) => {
    setFormData({ ...formData, image_url: imageName });
    setImagePreview(`${REACT_APP_API_BASE_URL}uploads/newsimages/${imageName}`);
  };

  const handleEditorChange = (content) => {
    setFormData({ ...formData, news_content: content });
  };

  const handlePremiumChange = (e) => {
    setFormData({ ...formData, premium: e.target.checked });
  };

  const normalizeHtml = (html = "") =>
    String(html)
      .replace(/&nbsp;/g, " ")
      .replace(/\u00a0/g, " ")
      .trim();

  // Fetch dropdowns
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [tokensRes, sectorsRes, industriesRes, categoriesRes, ntfRes] = await Promise.all([
          newsService.getInternalTokens(),
          newsService.getTagSectors(),
          newsService.getTagIndustries(),
          newsService.getCategories(),
          newsService.getAllNotificationTypes(),
        ]);
        setInternalTokens(tokensRes.data || []);
        setTagSectors(sectorsRes.data || []);
        setTagIndustries(industriesRes.data || []);
        setCategories(categoriesRes.categories || []);
        setNotificationTypes(ntfRes || []);
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
      }
    };
    fetchDropdowns();
  }, []);

  // Fetch announcement and pre-fill form
  useEffect(() => {
    if (!announcementId) return;

    const fetchAnnouncement = async () => {
      setFetchingData(true);
      try {
        const response = await api.get(`/corporate-announcements/raw/${announcementId}`);
        const announcement = response.data;
        setAnnouncementData(announcement);

        // Pre-fill form from announcement
        const pubDate = announcement.pub_date ? new Date(announcement.pub_date) : new Date();
        const dateStr = pubDate.toISOString().split('T')[0];
        const timeStr = pubDate.toTimeString().substring(0, 5);

        setFormData(prev => ({
          ...prev,
          title: announcement.title || "",
          brief_description: announcement.description || "",
          news_content: `<p>${announcement.description || ""}</p>`,
          link: announcement.link || "",
          source_url: announcement.link || "",
          publication_date: dateStr,
          publish_time: timeStr,
        }));
      } catch (error) {
        console.error("Error fetching announcement:", error);
        toast.error("❌ Failed to load announcement data");
      } finally {
        setFetchingData(false);
      }
    };
    fetchAnnouncement();
  }, [announcementId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.news_category) {
      toast.error("Please select a news category");
      return;
    }

    const action = e.nativeEvent.submitter?.value || "save";
    const isPublish = action === "publish";

    const data = new FormData();

    const convertToJSONArray = (value) => {
      if (!value) return "[]";
      if (Array.isArray(value)) {
        return JSON.stringify(value.filter(item => item !== ""));
      }
      return JSON.stringify(
        value.split(",").map(item => item.trim()).filter(item => item !== "")
      );
    };

    data.append("publisher", formData.publisher);
    data.append("title", formData.title);
    data.append("link", formData.link);
    data.append("publication_date", formData.publication_date);
    data.append("publish_time", formData.publish_time);
    data.append("brief_description", formData.brief_description);
    data.append("news_content", normalizeHtml(formData.news_content));
    data.append("news_category", formData.news_category);
    data.append("credit", formData.credit);
    data.append("source_url", formData.source_url);
    data.append("destination_url", formData.destination_url);
    data.append("premium", formData.premium || false);

    data.append("tags_stocks", convertToJSONArray(formData.tags_stocks));
    data.append("tags_sectors", convertToJSONArray(formData.tags_sectors));
    data.append("tags_industries", convertToJSONArray(formData.tags_industries));



    if (formData.image_url instanceof File) {
      data.append("image_url", formData.image_url);
    } else if (typeof formData.image_url === "string") {
      data.append("image_url", formData.image_url);
    }

    try {
      setLoading(true);
      const userId = localStorage.getItem("userId");
      const createResponse = await newsService.createNews(data);

      // If publishing, also call the publish endpoint to set ispublished=1 in news_feed table
      if (isPublish && createResponse?.news?.news_id) {
        await newsService.isPublishNews(createResponse.news.news_id, { published_by: userId });
      }

      // Mark the corporate announcement as published
      if (announcementId) {
        await api.put(`/corporate-announcements/raw/${announcementId}/publish`);
      }

      toast.success(
        isPublish
          ? "✅ Announcement published as news successfully"
          : "✅ Announcement saved as news draft"
      );

      if (isPublish && formData.ntf_type) {
        const ntfType = notificationTypes.find(t => t.id === formData.ntf_type);
        if (ntfType) {
          try {
            await newsService.sendNotification({
              id: 1,
              title: `${newsCategory} Update`,
              message: formData.title,
              query: ntfType.id,
              notificationtypeid: 4,
            });
          } catch (err) {
            console.error("Notification failed:", err);
          }
        }
      }

      if (onSubmitted) onSubmitted();
    } catch (error) {
      const msg = error.message || "Error publishing announcement";
      toast.error("❌ " + msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="news-container">
        <div className="flex-center" style={{ fontSize: '18px', color: '#5f6368', minHeight: '60vh' }}>
          Loading announcement data...
        </div>
      </div>
    );
  }

  return (
    <div className="news-container">
      <ToastContainer position="bottom-right" autoClose={2000} />


      {announcementData && (
        <div className="source-info-box">
          <strong>Source:</strong> {announcementData.title} — {announcementData.subject || 'N/A'}
          {announcementData.link && (
            <span> | <a href={announcementData.link} target="_blank" rel="noreferrer" className="text-primary">View PDF</a></span>
          )}
        </div>
      )}

      <div className="relative-container">

        <form className="news-card" onSubmit={handleSubmit}>
          {loading && <div className="loading-overlay">Processing...</div>}

          <div className="image-section-container">
            <label className="section-label">Featured Image</label>
            <div className="image-section" onClick={() => document.getElementById('imageInput').click()}>
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="preview-image" />
                </div>
              ) : (
                <div className="image-placeholder">
                  <span>Upload a Featured Image</span>
                </div>
              )}
              <input
                id="imageInput" type="file" accept="image/*" name="image_url"
                onChange={handleChange} className="image-input display-none"
              />
            </div>
            <div className="image-actions">
              <button type="button" className="media-library-btn"
                onClick={(e) => { e.stopPropagation(); setIsMediaLibraryOpen(true); }}>
                📁 Browse Media Library
              </button>
              {imagePreview && (
                <button type="button" className="clear-image-btn"
                  onClick={(e) => { e.stopPropagation(); setImagePreview(null); setFormData({ ...formData, image_url: null }); }}>
                  ✖ Remove Image
                </button>
              )}
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Category <span className="text-red"></span></label>
              <Select
                name="news_category"
                value={categories.map((cat) => ({ label: cat.name, value: cat.id })).find((option) => String(option.value) === String(formData.news_category)) || null}
                onChange={(selectedOption) => {
                  setFormData({ ...formData, news_category: selectedOption ? selectedOption.value : "" });
                  setNewsCategory(selectedOption ? selectedOption.label : "");
                }}
                options={categories.map((cat) => ({ label: cat.name, value: cat.id }))}
                placeholder="Select Category..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
                required
              />
            </div>
            <div className="form-group">
              <label>Publisher</label>
              <input name="publisher" value={formData.publisher} onChange={handleChange} placeholder="Enter publisher name" />
            </div>
            <div className="form-group">
              <label>Credit</label>
              <input name="credit" value={formData.credit} onChange={handleChange} placeholder="Credit (e.g. Author / Agency)" />
            </div>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input name="title" required value={formData.title} onChange={handleChange} placeholder="Enter an eye-catching headline" />
          </div>

          <div className="form-group">
            <label>Brief Description</label>
            <textarea name="brief_description" rows="2" value={formData.brief_description}
              onChange={handleChange} placeholder="Short summary of the news..." required />
          </div>

          <div className="form-group quill-editor-group">
            <label>Full News Content</label>
            <ReactQuill
              theme="snow"
              value={formData.news_content}
              onChange={handleEditorChange}
              placeholder="Write or paste full news content here..."
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link', 'image', 'video'],
                  [{ align: ['justify'] }],
                  ['clean'],
                ],
              }}
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Link</label>
              <input name="link" value={formData.link} onChange={handleChange} placeholder="Original article link" />
            </div>
            <div className="form-group">
              <label>Source URL</label>
              <input name="source_url" value={formData.source_url} onChange={handleChange} placeholder="Source link" />
            </div>
            <div className="form-group">
              <label>Destination URL</label>
              <input name="destination_url" value={formData.destination_url} onChange={handleChange} placeholder="Redirect destination" />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Publication Date</label>
              <input type="date" name="publication_date" value={formData.publication_date} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Publish Time</label>
              <input type="time" name="publish_time" value={formData.publish_time} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Notification Type</label>
              <Select
                name="tags_notification_type_id"
                value={formData.ntf_type ? {
                  label: notificationTypes.find((item) => item.id === formData.ntf_type)?.query_title || "",
                  value: formData.ntf_type,
                } : null}
                onChange={(selectedOption) => setFormData({ ...formData, ntf_type: selectedOption ? selectedOption.value : null })}
                options={notificationTypes.map((item) => ({ label: item.query_title || "", value: item.id }))}
                placeholder="Select Notification Type..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
              />
            </div>
          </div>

          <h3 className="tags-heading">Tags</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Stocks</label>
              <Select
                name="tags_stocks" isMulti
                value={formData.tags_stocks && Array.isArray(formData.tags_stocks)
                  ? internalTokens.map((token) => ({ label: `${token.symbol} (${token.name})`, value: token.token })).filter((option) => formData.tags_stocks.includes(option.value))
                  : []}
                onChange={(selectedOptions) => setFormData({ ...formData, tags_stocks: selectedOptions ? selectedOptions.map((opt) => opt.value) : [] })}
                options={internalTokens.map((token) => ({ label: `${token.symbol} (${token.name})`, value: token.token }))}
                placeholder="Select Stocks..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
              />
            </div>
            <div className="form-group">
              <label>Sectors</label>
              <Select
                name="tags_sectors" isMulti
                value={formData.tags_sectors && Array.isArray(formData.tags_sectors)
                  ? tagSectors.map((item) => ({ label: item.sector_name || item, value: item.sector_name || item })).filter((option) => formData.tags_sectors.includes(option.value))
                  : []}
                onChange={(selectedOptions) => setFormData({ ...formData, tags_sectors: selectedOptions ? selectedOptions.map((opt) => opt.value) : [] })}
                options={tagSectors.map((item) => ({ label: item.sector_name || item, value: item.sector_name || item }))}
                placeholder="Select Sectors..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
              />
            </div>
            <div className="form-group">
              <label>Industries</label>
              <Select
                name="tags_industries" isMulti
                value={formData.tags_industries && Array.isArray(formData.tags_industries)
                  ? tagIndustries.map((item) => ({ label: item.industry_name || item, value: item.industry_name || item })).filter((option) => formData.tags_industries.includes(option.value))
                  : []}
                onChange={(selectedOptions) => setFormData({ ...formData, tags_industries: selectedOptions ? selectedOptions.map((opt) => opt.value) : [] })}
                options={tagIndustries.map((item) => ({ label: item.industry_name || item, value: item.industry_name || item }))}
                placeholder="Select Industries..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
              />
            </div>
          </div>

          <div className="form-group form-group-premium">
            <label className="premium-flex">
              <input type="checkbox" id="premium" name="premium" checked={!!formData.premium}
                onChange={handlePremiumChange} className="premium-checkbox" />
              <span className="premium-label">
                Premium News
              </span>
            </label>
          </div>

          <div className="flex-space-around">
            <button type="button" className="submit-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              Save as Draft
            </button>
            <button type="submit" className="publish-btn" value="publish">
              Publish
            </button>
          </div>
        </form>
      </div>

      <MediaLibraryModal
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
      />
    </div>
  );
};

export default CorporateAnnouncementForm;
